import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Watchlist.css';

function Watchlist() {
  const [bookmarks, setBookmarks] = useState([]);
  const [url, setUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchBookmarks = useCallback(async () => {
    try {
      const params = { page: 0, size: 50 };
      if (filter === 'unread') params.unreadOnly = true;
      const response = await axios.get('/api/bookmarks', { params });
      setBookmarks(response.data.content || []);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await axios.get('/api/bookmarks/unread-count');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchBookmarks();
    fetchUnreadCount();
  }, [fetchBookmarks, fetchUnreadCount]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSaving(true);
    try {
      await axios.post('/api/bookmarks', { url: url.trim(), notes: notes.trim() || null });
      setUrl('');
      setNotes('');
      fetchBookmarks();
      fetchUnreadCount();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save bookmark');
    } finally {
      setSaving(false);
    }
  };

  const toggleRead = async (id, isRead) => {
    try {
      await axios.patch(`/api/bookmarks/${id}/read?isRead=${!isRead}`);
      fetchBookmarks();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error updating bookmark:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove from watchlist?')) return;
    try {
      await axios.delete(`/api/bookmarks/${id}`);
      fetchBookmarks();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    }
  };

  return (
    <div className="watchlist-container">
      <div className="watchlist-content">
        <div className="watchlist-header">
          <h2>Watchlist</h2>
          <p>Save links to watch or read later. Earn +3 points per save.</p>
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount} unread</span>
          )}
        </div>

        <form className="add-bookmark-form" onSubmit={handleAdd}>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Paste a URL (article, video, etc.)"
            required
          />
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note"
            maxLength="300"
          />
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save to Watchlist (+3 pts)'}
          </button>
        </form>

        <div className="filter-tabs">
          <button
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={filter === 'unread' ? 'active' : ''}
            onClick={() => setFilter('unread')}
          >
            Unread ({unreadCount})
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading watchlist...</div>
        ) : bookmarks.length === 0 ? (
          <div className="watchlist-empty">
            <h3>Your watchlist is empty</h3>
            <p>Paste a URL above to save it for later.</p>
          </div>
        ) : (
          <div className="bookmark-list">
            {bookmarks.map(item => (
              <div key={item.id} className={`bookmark-card ${item.isRead ? 'read' : ''}`}>
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="bookmark-link">
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt="" className="bookmark-image" />
                  )}
                  <div className="bookmark-body">
                    <h3>{item.title || item.url}</h3>
                    {item.description && <p className="bookmark-desc">{item.description}</p>}
                    <span className="bookmark-url">{item.url}</span>
                    {item.notes && <p className="bookmark-notes">Note: {item.notes}</p>}
                  </div>
                </a>
                <div className="bookmark-actions">
                  <button
                    className="action-btn"
                    onClick={() => toggleRead(item.id, item.isRead)}
                  >
                    {item.isRead ? 'Mark Unread' : 'Mark as Read'}
                  </button>
                  <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Watchlist;
